import { DateTime } from 'luxon';
import { TicketPurchaseDTO } from '../DTO/tickets/TicketPurchaseDTO';
import { TicketsGroupDTO } from '../DTO/tickets/TicketsGroupDTO';

export interface PurchaseSlotDisplay {
  name?: string | null;
  date?: string | null;
  time?: string | null;
  endTime?: string | null;
}

function readRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' ? value as Record<string, unknown> : undefined;
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function readTicketsGroupFromRaw(raw: Record<string, unknown> | undefined): TicketsGroupDTO | null {
  if (!raw) {
    return null;
  }

  const group = (raw['ticketsGroup'] ?? raw['TicketsGroup']) as TicketsGroupDTO | null | undefined;
  return group ?? null;
}

function readTicketsGroupIdFromRaw(raw: Record<string, unknown> | undefined): number | null {
  if (!raw) {
    return null;
  }

  const value = raw['ticketsGroupId'] ?? raw['TicketsGroupId'];
  return typeof value === 'number' ? value : null;
}

export function readTicketGroupsFromRaw(raw: Record<string, unknown> | undefined): TicketsGroupDTO[] {
  if (!raw) {
    return [];
  }

  const groups = (raw['ticketsGroups'] ?? raw['TicketsGroups']) as TicketsGroupDTO[] | undefined;
  return Array.isArray(groups) ? groups : [];
}

export function parseTicketsGroupSlot(group?: TicketsGroupDTO | null): PurchaseSlotDisplay | null {
  if (!group) {
    return null;
  }

  const groupRaw = readRecord(group);
  const startTime = readString(group.startTime) ?? readString(groupRaw?.['StartTime']);
  const endTimeRaw = readString(group.endTime) ?? readString(groupRaw?.['EndTime']);
  const name = readString(group.name) ?? readString(groupRaw?.['Name']);

  if (startTime) {
    const start = DateTime.fromISO(startTime);
    if (!start.isValid) {
      return name ? { name } : null;
    }

    return {
      name: name ?? null,
      date: start.toFormat('dd/MM/yyyy'),
      time: start.toFormat('HH:mm'),
      endTime: endTimeRaw ? DateTime.fromISO(endTimeRaw).toFormat('HH:mm') : null,
    };
  }

  return name ? { name } : null;
}

export function resolvePurchaseSlot(
  purchase: TicketPurchaseDTO,
  ticketGroups?: TicketsGroupDTO[] | null,
): PurchaseSlotDisplay | null {
  const purchaseRaw = readRecord(purchase);
  let group = readTicketsGroupFromRaw(purchaseRaw);

  if (!group) {
    const groupId = readTicketsGroupIdFromRaw(purchaseRaw);
    if (groupId != null && ticketGroups?.length) {
      group = ticketGroups.find(item => item.id === groupId) ?? null;
    }
  }

  return parseTicketsGroupSlot(group);
}

export function formatPurchaseSlotLabel(slot: PurchaseSlotDisplay): string {
  const parts: string[] = [];

  if (slot.name) {
    parts.push(slot.name);
  }

  if (slot.date && slot.time) {
    const schedule = slot.endTime
      ? `${slot.date} - ${slot.time} - ${slot.endTime}`
      : `${slot.date} - ${slot.time}`;
    parts.push(schedule);
  } else if (slot.date) {
    parts.push(slot.date);
  }

  return parts.join(' · ');
}
